import { useEffect, useState } from "react";
import { ApiError, reviseDocPortal } from "../../api";

const MIN_NOTE_CHARS = 10;

interface DocPortalReviseModalProps {
  dealId: string;
  docKey: string;
  /** Doc's display_name from the active portal — used in the modal title. */
  docDisplayName: string;
  /** Fires on successful revise. Parent closes the modal + registers the
   *  optimistic overlay + toasts. */
  onConfirmed: () => void;
  onCancel: () => void;
}

export function DocPortalReviseModal({
  dealId,
  docKey,
  docDisplayName,
  onConfirmed,
  onCancel,
}: DocPortalReviseModalProps) {
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Soft validation message — surfaces only after the user attempts submit.
  // Avoids nagging on the empty initial state.
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  // Lazy: one key for the lifetime of this modal. Retries after errors reuse
  // it so Portal dedupes via its 24h TTL — protects against double-revise if
  // the first call landed but the response was lost.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, submitting]);

  // Clear stale validation message as the user types past the threshold —
  // immediate positive feedback without making them re-submit to see it.
  useEffect(() => {
    if (validationMessage && note.trim().length >= MIN_NOTE_CHARS) {
      setValidationMessage(null);
    }
  }, [note, validationMessage]);

  const handleSubmit = async () => {
    const trimmed = note.trim();
    if (trimmed.length < MIN_NOTE_CHARS) {
      setValidationMessage(
        `Note must be at least ${MIN_NOTE_CHARS} characters (currently ${trimmed.length}).`,
      );
      return;
    }
    setError(null);
    setValidationMessage(null);
    setSubmitting(true);
    try {
      await reviseDocPortal(dealId, docKey, trimmed, idempotencyKey);
      onConfirmed();
      // Parent unmounts on success.
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "revise_failed",
      );
      setSubmitting(false);
    }
  };

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card doc-portal-revise-card">
        <div className="write-confirm-title">
          REQUEST REVISION: <span className="doc-portal-revise-target">{docDisplayName}</span>
        </div>
        <div className="doc-portal-revise-body">
          The borrower will receive an email with your note explaining what to
          fix. The doc status flips to REVISION_REQUESTED until they re-upload.
        </div>
        <label className="doc-portal-revise-label">
          <span>Revision note <span className="doc-portal-revise-required">*</span></span>
          <textarea
            className="doc-portal-revoke-reason"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What needs to be different about the upload (e.g. 'photo is too blurry, please retake in better light')…"
            rows={4}
            disabled={submitting}
            autoFocus
          />
        </label>
        {validationMessage && (
          <div className="doc-portal-revise-validation">⚠ {validationMessage}</div>
        )}
        {error && <div className="write-confirm-error">⚠ {error}</div>}
        <div className="write-confirm-actions">
          <button
            className="write-confirm-cancel"
            onClick={onCancel}
            disabled={submitting}
          >
            CANCEL
          </button>
          <button
            className="write-confirm-confirm doc-portal-revise-submit"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "REQUESTING…" : "REQUEST REVISION"}
          </button>
        </div>
      </div>
    </div>
  );
}
